// client/src/components/paymentUploads/PaymentUploadsList.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import paymentUploadService from '../../services/paymentUploadService';

const PaymentUploadsList = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  
  const [paymentUploads, setPaymentUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadToDelete, setUploadToDelete] = useState(null);
  
  // Load payment uploads on component mount
  useEffect(() => {
    loadPaymentUploads();
  }, []);
  
  // Function to load payment uploads
  const loadPaymentUploads = async () => {
    try {
      setLoading(true);
      const response = await paymentUploadService.getPaymentUploads();
      setPaymentUploads(response.data);
      setError('');
    } catch (err) {
      setError(t('errorLoadingPaymentUploads'));
    } finally {
      setLoading(false);
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t('notSpecified');
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  
  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Handle delete click
  const handleDeleteClick = (upload) => {
    setUploadToDelete(upload);
    setDeleteDialogOpen(true);
  };
  
  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    try {
      await paymentUploadService.deletePaymentUpload(uploadToDelete.id);
      setSuccess(t('paymentUploadDeletedSuccess'));
      loadPaymentUploads();
    } catch (err) {
      setError(t('errorDeletingPaymentUpload'));
    } finally {
      setDeleteDialogOpen(false);
      setUploadToDelete(null);
    }
  };
  
  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUploadToDelete(null);
  };
  
  // Handle close alert
  const handleCloseAlert = () => {
    setSuccess('');
    setError('');
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('paymentUploads')}
      </Typography>
      
      {/* Alert messages */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Add Payment Upload button */}
      <Box sx={{ mb: 2, textAlign: direction === 'ltr' ? 'right' : 'left' }}>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/payment-uploads/create"
        >
          {t('uploadPaymentFile')}
        </Button>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : paymentUploads.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            {t('noPaymentUploadsFound')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('id')}</TableCell>
                <TableCell>{t('fileName')}</TableCell>
                <TableCell align="center">{t('totalRecords')}</TableCell>
                <TableCell align="center">{t('matchedRecords')}</TableCell>
                <TableCell align="center">{t('status')}</TableCell>
                <TableCell>{t('createdAt')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentUploads.map((upload) => (
                <TableRow key={upload.id}>
                  <TableCell>{upload.id}</TableCell>
                  <TableCell>{upload.file_name}</TableCell>
                  <TableCell align="center">{upload.total_records}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={`${upload.matched_records}/${upload.total_records}`}
                      color={upload.matched_records === upload.total_records ? "success" : "warning"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={t(upload.status)}
                      color={getStatusColor(upload.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(upload.created_at)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('view')}>
                      <IconButton
                        color="primary"
                        component={RouterLink}
                        to={`/payment-uploads/${upload.id}`}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('delete')}>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(upload)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>{t('confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deletePaymentUploadConfirmation', { 
              fileName: uploadToDelete?.file_name 
            })}
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('deletePaymentUploadWarning')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaymentUploadsList;